import React, { useEffect, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { useTranslation } from "react-i18next";

const geoUrl = "/world-110m.json";

const CONTINENT_GROUPS = [
  {
    key: "europe",
    isos: ["DEU", "GBR", "CHE", "POL", "ITA", "GEO", "GRC", "RUS"],
  },
  { key: "asia", isos: ["CHN", "JPN", "MYS", "SGP", "THA"] },
  { key: "northAmerica", isos: ["USA", "CAN"] },
  { key: "southAmerica", isos: ["BRA", "COL"] },
  { key: "oceania", isos: ["AUS", "NZL"] },
  { key: "africa", isos: ["ZAF"] },
];

function groupByContinent(members) {
  const grouped = Object.fromEntries(
    CONTINENT_GROUPS.map(({ key }) => [key, []])
  );
  members.forEach((member) => {
    CONTINENT_GROUPS.forEach(({ key, isos }) => {
      if (isos.includes(member.iso)) grouped[key].push(member);
    });
  });
  return grouped;
}

function orderedContinentEntries(grouped) {
  return CONTINENT_GROUPS.map(({ key }) => [key, grouped[key] || []]).filter(
    ([, countries]) => countries.length > 0
  );
}

const members = [
  {
    iso: "DEU",
    coordinates: [10.4515, 51.1657],
    flag: "/images/flags/DE.png",
    capital: "Berlin",
    population: "83 Millionen",
    area: "357.022 km²",
  },
  {
    iso: "CHN",
    coordinates: [104.1954, 35.8617],
    flag: "/images/flags/CN.png",
    capital: "Peking",
    population: "1,4 Milliarden",
    area: "9.596.961 km²",
  },
  {
    iso: "USA",
    coordinates: [-95.7129, 37.0902],
    flag: "/images/flags/US.png",
    capital: "Washington D.C.",
    population: "331 Millionen",
    area: "9.833.520 km²",
  },
  {
    iso: "AUS",
    coordinates: [133.7751, -25.2744],
    flag: "/images/flags/AU.png",
    capital: "Canberra",
    population: "26 Millionen",
    area: "7.692.024 km²",
  },
  {
    iso: "BRA",
    coordinates: [-51.9253, -14.235],
    flag: "/images/flags/BR.png",
    capital: "Brasília",
    population: "215 Millionen",
    area: "8.515.767 km²",
  },
  {
    iso: "COL",
    coordinates: [-74.2973, 4.5709],
    flag: "/images/flags/CO.png",
    capital: "Bogotá",
    population: "51 Millionen",
    area: "1.141.748 km²",
  },
  {
    iso: "CAN",
    coordinates: [-106.3468, 56.1304],
    flag: "/images/flags/CA.png",
    capital: "Ottawa",
    population: "38 Millionen",
    area: "9.984.670 km²",
  },
  {
    iso: "JPN",
    coordinates: [138.2529, 36.2048],
    flag: "/images/flags/JP.png",
    capital: "Tokio",
    population: "125 Millionen",
    area: "377.975 km²",
  },
  {
    iso: "NZL",
    coordinates: [174.8859, -40.9006],
    flag: "/images/flags/NZ.png",
    capital: "Wellington",
    population: "5 Millionen",
    area: "268.021 km²",
  },
  {
    iso: "MYS",
    coordinates: [101.9758, 4.2105],
    flag: "/images/flags/MY.png",
    capital: "Kuala Lumpur",
    population: "33 Millionen",
    area: "330.803 km²",
  },
  {
    iso: "GBR",
    coordinates: [-3.435973, 55.378051],
    flag: "/images/flags/GB.png",
    capital: "London",
    population: "67 Millionen",
    area: "243.610 km²",
  },
  {
    iso: "CHE",
    coordinates: [8.2275, 46.8182],
    flag: "/images/flags/CH.png",
    capital: "Bern",
    population: "8,7 Millionen",
    area: "41.290 km²",
  },
  {
    iso: "POL",
    coordinates: [19.1451, 51.9194],
    flag: "/images/flags/PL.png",
    capital: "Warschau",
    population: "38 Millionen",
    area: "312.696 km²",
  },
  {
    iso: "SGP",
    coordinates: [103.8198, 1.3521],
    flag: "/images/flags/SG.png",
    capital: "Singapur",
    population: "5,7 Millionen",
    area: "719 km²",
  },
  {
    iso: "ITA",
    coordinates: [12.5674, 41.8719],
    flag: "/images/flags/IT.png",
    capital: "Rom",
    population: "59 Millionen",
    area: "301.340 km²",
  },
  {
    iso: "ZAF",
    coordinates: [22.9375, -30.5595],
    flag: "/images/flags/ZA.png",
    capital: "Kapstadt / Pretoria",
    population: "60 Millionen",
    area: "1.219.090 km²",
  },
  {
    iso: "GEO",
    coordinates: [43.3569, 42.3154],
    flag: "/images/flags/GE.png",
    capital: "Tiflis",
    population: "3,7 Millionen",
    area: "69.700 km²",
  },
  {
    iso: "GRC",
    coordinates: [21.8243, 39.0742],
    flag: "/images/flags/GR.png",
    capital: "Athen",
    population: "10,7 Millionen",
    area: "131.957 km²",
  },
  {
    iso: "THA",
    coordinates: [100.9925, 15.87],
    flag: "/images/flags/TH.png",
    capital: "Bangkok",
    population: "70 Millionen",
    area: "513.120 km²",
  },
  {
    iso: "RUS",
    coordinates: [105.3188, 61.524],
    flag: "/images/flags/RU.png",
    capital: "Moskau",
    population: "146 Millionen",
    area: "17.098.242 km²",
  },
];

function countryNameFromT(t, iso) {
  const raw = t(`chinwooWorldMap.countries.${iso}`, iso);
  return typeof raw === "string" ? raw : iso;
}

function ChinWooWorldMap() {
  const { t } = useTranslation();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(true);
  const [highlightedCountry, setHighlightedCountry] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);

  const groupedMembers = useMemo(() => groupByContinent(members), []);
  const continentEntries = useMemo(
    () => orderedContinentEntries(groupedMembers),
    [groupedMembers]
  );

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains("dark"));
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduceMotion(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  const primaryColor = isDarkMode ? "96, 165, 250" : "59, 130, 246";
  const nonMemberColor = "148, 163, 184";
  const transition = reduceMotion ? "none" : "all 0.3s ease";

  const selectedMember = selectedCountry
    ? members.find((m) => m.iso === selectedCountry)
    : null;

  const listButtonClasses = (isSelected, isHighlighted) => {
    const base =
      "w-full rounded-2xl border border-slate-200 border-l-4 bg-white px-4 py-3 text-left shadow-sm transition-all duration-300 dark:border-slate-700 dark:bg-slate-800/80 " +
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-50 " +
      "dark:focus-visible:ring-primary-light/50 dark:focus-visible:ring-offset-slate-900 " +
      "motion-safe:hover:-translate-y-px motion-safe:hover:shadow-md ";
    if (isSelected) {
      return `${base} border-l-primary shadow-md dark:border-l-primary-light`;
    }
    if (isHighlighted) {
      return `${base} border-l-slate-400 bg-slate-50 dark:border-slate-500 dark:bg-slate-700/40`;
    }
    return `${base} border-l-slate-300 dark:border-l-slate-600 motion-safe:hover:border-slate-300 dark:motion-safe:hover:border-slate-500`;
  };

  return (
    <section
      className="relative z-10 bg-slate-50/50 pb-14 pt-12 transition-colors dark:bg-slate-900 md:pb-16 md:pt-14"
      id="chinwoo-world-map"
      aria-labelledby="chinwoo-world-map-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="mb-10 text-center md:mb-12">
          <h2
            id="chinwoo-world-map-heading"
            className="relative mb-5 inline-block text-4xl font-semibold tracking-tight text-primary dark:text-primary-light md:text-5xl"
          >
            {t("chinwooWorldMap.title")}
            <span
              className="absolute -bottom-2.5 left-1/2 h-0.5 w-16 -translate-x-1/2 rounded-full bg-primary dark:bg-primary-light"
              aria-hidden
            />
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-lg leading-relaxed text-slate-600 dark:text-slate-400">
            {t("chinwooWorldMap.description")}
          </p>
        </header>

        {/*
          lg: Höhe kommt nur von der Karten-Box (im Fluss). Listen-Box absolut gleiche Höhe (h-full).
          calc: 3 Spalten à 1fr, gap-8 = 2rem, zwei Zwischenräume → Spalte1 = (100% - 4rem) / 3
        */}
        <div className="flex flex-col gap-6 lg:relative lg:min-h-0">
          <div className="order-1 min-w-0 lg:absolute lg:left-0 lg:top-0 lg:z-10 lg:h-full lg:w-[calc((100%-4rem)/3)] lg:min-w-0">
            <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
              <div className="flex-shrink-0 border-b border-slate-200 px-5 py-4 dark:border-slate-700">
                <h3
                  id="chinwoo-world-map-list-title"
                  className="text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100"
                >
                  {t("chinwooWorldMap.memberCountries")}
                </h3>
              </div>
              <nav
                className="max-h-[min(32rem,70vh)] flex-1 overflow-y-auto overscroll-contain p-4 md:p-5 lg:max-h-none lg:min-h-0"
                aria-labelledby="chinwoo-world-map-list-title"
                aria-label={t("chinwooWorldMap.list_label")}
              >
                {continentEntries.map(([continentKey, countries], index) => (
                  <div
                    key={continentKey}
                    className={
                      index > 0
                        ? "mt-6 border-t border-slate-200 pt-6 dark:border-slate-700"
                        : ""
                    }
                  >
                    <h4 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      <span
                        className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary-light/15"
                        aria-hidden
                      >
                        <i className="fas fa-globe text-sm text-primary dark:text-primary-light" />
                      </span>
                      {t(`chinwooWorldMap.continents.${continentKey}`)}
                    </h4>
                    <ul className="m-0 list-none space-y-2 p-0">
                      {countries.map((member) => {
                        const countryName = countryNameFromT(t, member.iso);
                        const isHighlighted = highlightedCountry === member.iso;
                        const isSelected = selectedCountry === member.iso;
                        return (
                          <li key={member.iso}>
                            <button
                              type="button"
                              aria-pressed={isSelected}
                              aria-label={t("chinwooWorldMap.select_country_aria", {
                                country: countryName,
                              })}
                              className={`flex items-center gap-3 ${listButtonClasses(
                                isSelected,
                                isHighlighted
                              )}`}
                              onClick={() => setSelectedCountry(member.iso)}
                              onMouseEnter={() =>
                                setHighlightedCountry(member.iso)
                              }
                              onMouseLeave={() => setHighlightedCountry(null)}
                              onFocus={() => setHighlightedCountry(member.iso)}
                              onBlur={() => setHighlightedCountry(null)}
                            >
                              <img
                                src={member.flag}
                                alt=""
                                width={40}
                                height={40}
                                className={`h-10 w-10 flex-shrink-0 rounded-full border-2 object-cover shadow-sm transition-transform duration-300 ${
                                  isSelected || isHighlighted
                                    ? "border-primary motion-safe:scale-105 dark:border-primary-light"
                                    : "border-slate-200 dark:border-slate-600"
                                }`}
                              />
                              <span
                                className={`min-w-0 flex-1 break-words text-sm font-medium ${
                                  isSelected || isHighlighted
                                    ? "text-primary dark:text-primary-light"
                                    : "text-slate-900 dark:text-slate-100"
                                }`}
                              >
                                {countryName}
                              </span>
                              {(isSelected || isHighlighted) && (
                                <i
                                  className="fas fa-map-marker-alt flex-shrink-0 text-xs text-primary dark:text-primary-light"
                                  aria-hidden
                                />
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </nav>
            </div>
          </div>

          <div className="order-2 min-w-0 lg:ml-[calc((100%-4rem)/3+2rem)]">
            <div className="w-full rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800 md:p-6 lg:p-8">
              <figure
                className="m-0"
                role="group"
                aria-label={t("chinwooWorldMap.map_label")}
              >
                <div className="relative mb-6 w-full overflow-hidden rounded-xl border border-slate-100 bg-slate-50/80 dark:border-slate-600 dark:bg-slate-900/40">
                  <ComposableMap
                    projectionConfig={{ scale: 150 }}
                    width={800}
                    height={400}
                    className="h-auto w-full drop-shadow-sm"
                  >
                    <Geographies geography={geoUrl}>
                      {({ geographies }) => {
                        const seenCountries = new Set();
                        const filteredGeographies = geographies.filter((geo) => {
                          const countryISO = geo.properties.adm0_a3;
                          if (seenCountries.has(countryISO)) return false;
                          seenCountries.add(countryISO);
                          return true;
                        });

                        return filteredGeographies.map((geo) => {
                          const countryISO = geo.properties.adm0_a3;
                          const member = members.find(
                            (m) => m.iso === countryISO
                          );
                          const isHighlighted =
                            member && highlightedCountry === countryISO;
                          const isSelected =
                            member && selectedCountry === countryISO;

                          const nonMemberFill = `rgba(${nonMemberColor}, ${
                            isDarkMode ? 0.15 : 0.1
                          })`;
                          const nonMemberStroke = `rgba(${nonMemberColor}, ${
                            isDarkMode ? 0.4 : 0.3
                          })`;
                          const nonMemberStyle = {
                            default: {
                              fill: nonMemberFill,
                              stroke: nonMemberStroke,
                              strokeWidth: 1.2,
                              outline: "none",
                              cursor: "default",
                            },
                            hover: {
                              fill: nonMemberFill,
                              stroke: nonMemberStroke,
                              strokeWidth: 1.2,
                              outline: "none",
                              cursor: "default",
                            },
                            pressed: {
                              fill: nonMemberFill,
                              stroke: nonMemberStroke,
                              strokeWidth: 1.2,
                              outline: "none",
                              cursor: "default",
                            },
                          };

                          const memberStyle = {
                            default: {
                              fill: `rgba(${primaryColor}, ${
                                isSelected
                                  ? isDarkMode
                                    ? 0.65
                                    : 0.6
                                  : isHighlighted
                                    ? isDarkMode
                                      ? 0.6
                                      : 0.55
                                    : isDarkMode
                                      ? 0.4
                                      : 0.35
                              })`,
                              stroke: `rgba(${primaryColor}, ${
                                isSelected
                                  ? isDarkMode
                                    ? 1
                                    : 0.9
                                  : isHighlighted
                                    ? isDarkMode
                                      ? 0.9
                                      : 0.8
                                    : isDarkMode
                                      ? 0.6
                                      : 0.5
                              })`,
                              strokeWidth: isSelected
                                ? 2.5
                                : isHighlighted
                                  ? 2
                                  : 1.2,
                              outline: "none",
                              filter: isSelected
                                ? `drop-shadow(0 10px 15px rgba(${primaryColor}, 0.4))`
                                : isHighlighted
                                  ? `drop-shadow(0 4px 6px rgba(${primaryColor}, 0.3))`
                                  : `drop-shadow(0 4px 6px rgba(${primaryColor}, 0.2))`,
                              transform: isSelected
                                ? "scale(1.05)"
                                : isHighlighted
                                  ? "scale(1.02)"
                                  : "none",
                              transformOrigin: "center center",
                              transition,
                              cursor: "pointer",
                            },
                            hover: {
                              fill: `rgba(${primaryColor}, ${
                                isDarkMode ? 0.6 : 0.55
                              })`,
                              stroke: `rgba(${primaryColor}, ${
                                isDarkMode ? 0.9 : 0.8
                              })`,
                              strokeWidth: 2,
                              filter: `drop-shadow(0 4px 6px rgba(${primaryColor}, 0.3))`,
                              transform: reduceMotion ? "none" : "scale(1.02)",
                              transition,
                            },
                            pressed: {
                              fill: `rgba(${primaryColor}, ${
                                isDarkMode ? 0.65 : 0.6
                              })`,
                              stroke: `rgba(${primaryColor}, ${
                                isDarkMode ? 1 : 0.9
                              })`,
                              strokeWidth: 2.5,
                              filter: `drop-shadow(0 10px 15px rgba(${primaryColor}, 0.4))`,
                              transform: reduceMotion ? "none" : "scale(1.05)",
                              transition,
                            },
                          };

                          return (
                            <Geography
                              key={geo.rsmKey}
                              geography={geo}
                              onClick={
                                member
                                  ? () => setSelectedCountry(countryISO)
                                  : undefined
                              }
                              onMouseEnter={
                                member
                                  ? () => setHighlightedCountry(countryISO)
                                  : undefined
                              }
                              onMouseLeave={
                                member
                                  ? () => setHighlightedCountry(null)
                                  : undefined
                              }
                              style={member ? memberStyle : nonMemberStyle}
                            />
                          );
                        });
                      }}
                    </Geographies>
                  </ComposableMap>
                </div>
                <figcaption className="sr-only">
                  {t("chinwooWorldMap.alt")}
                </figcaption>
              </figure>

              {selectedMember && (
                <article
                  className="rounded-2xl border border-slate-200 border-l-4 border-l-primary bg-slate-50/60 p-5 dark:border-slate-600 dark:border-l-primary-light dark:bg-slate-900/40 md:p-6"
                  aria-labelledby="chinwoo-world-map-detail-title"
                >
                  {(() => {
                    const countryName = countryNameFromT(
                      t,
                      selectedMember.iso
                    );
                    return (
                      <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
                        <img
                          src={selectedMember.flag}
                          alt=""
                          width={64}
                          height={64}
                          className="h-16 w-16 flex-shrink-0 rounded-full border-2 border-primary/30 object-cover shadow-md dark:border-primary-light/30"
                        />
                        <div className="min-w-0 flex-1">
                          <h3
                            id="chinwoo-world-map-detail-title"
                            className="mb-4 break-words text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100 sm:text-2xl"
                          >
                            {countryName}
                          </h3>
                          <dl className="m-0 grid grid-cols-1 gap-4 sm:grid-cols-3">
                            <div className="flex min-w-0 items-start gap-3">
                              <span
                                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary-light/15"
                                aria-hidden
                              >
                                <i className="fas fa-building text-sm text-primary dark:text-primary-light" />
                              </span>
                              <div className="min-w-0">
                                <dt className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                  {t("chinwooWorldMap.field_capital")}
                                </dt>
                                <dd className="m-0 text-sm font-medium text-slate-900 dark:text-slate-100">
                                  {selectedMember.capital}
                                </dd>
                              </div>
                            </div>
                            <div className="flex min-w-0 items-start gap-3">
                              <span
                                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary-light/15"
                                aria-hidden
                              >
                                <i className="fas fa-users text-sm text-primary dark:text-primary-light" />
                              </span>
                              <div className="min-w-0">
                                <dt className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                  {t("chinwooWorldMap.field_population")}
                                </dt>
                                <dd className="m-0 text-sm font-medium text-slate-900 dark:text-slate-100">
                                  {selectedMember.population}
                                </dd>
                              </div>
                            </div>
                            <div className="flex min-w-0 items-start gap-3">
                              <span
                                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary-light/15"
                                aria-hidden
                              >
                                <i className="fas fa-map text-sm text-primary dark:text-primary-light" />
                              </span>
                              <div className="min-w-0">
                                <dt className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                  {t("chinwooWorldMap.field_area")}
                                </dt>
                                <dd className="m-0 break-words text-sm font-medium text-slate-900 dark:text-slate-100">
                                  {selectedMember.area}
                                </dd>
                              </div>
                            </div>
                          </dl>
                        </div>
                      </div>
                    );
                  })()}
                </article>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ChinWooWorldMap;
